/**
 * The pieces both editors share, and each one carries a rule.
 *
 * A provenance badge that cannot be edited away, a validation report that shows
 * every problem rather than the first, a change-note field that counts down to
 * twenty characters, and a condition builder that offers twelve operators and
 * never a thirteenth.
 *
 * Nothing here renders a condition into English. `explain()` lives on the
 * server and the builder asks it, for the same reason `readPeriod()` is the only
 * place a period becomes words: a second rendering is a second vocabulary, and
 * the two disagree the first time an operator's wording changes — in the
 * direction where a lawyer reviews a sentence the engine does not mean.
 */

import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import type { ConditionVocabulary, TemplateProblemView } from '../api/client'

/* ----------------------------------------------------------- provenance */

const LAYER_WORDS: Record<string, string> = {
  S: 'Statutory',
  O: 'Office',
  C: 'Case',
  D: 'Unsourced draft',
  'S/O': 'Pending sign-off',
}

const LAYER_TONE: Record<string, string> = {
  S: 'accent',
  O: 'neutral',
  C: 'good',
  D: 'warn',
  'S/O': 'warn',
}

/**
 * The five layers, and a sixth thing that is not a layer.
 *
 * `null` is the skeleton having tagged nothing — a masthead, a विषयः line, a
 * signature stack — and it renders as *untagged* rather than being guessed into
 * one of the five. Guessing would claim an origin nobody wrote down.
 */
export function LayerBadge({ provenance }: { provenance: string | null | undefined }) {
  if (!provenance) return <span className="chip neutral">Untagged</span>
  return (
    <span className={`chip ${LAYER_TONE[provenance] ?? 'neutral'}`} title={`[${provenance}]`}>
      <span className="mono">[{provenance}]</span> {LAYER_WORDS[provenance] ?? provenance}
    </span>
  )
}

/* --------------------------------------------------------- the problems */

/**
 * **Every problem, not the first.** An administrator gets one list and one
 * trip, which is the same reason `all` does not short-circuit in the condition
 * language.
 *
 * Errors and warnings are told apart because only one of them refuses
 * publication: the library carries five `[S]` blocks with no citation and one
 * `[C]` block with no instruction, and those are facts about the office's own
 * documents rather than mistakes in this draft.
 */
export function ValidationReport({
  problems,
  emptyText = 'Nothing outstanding.',
}: {
  problems: TemplateProblemView[]
  emptyText?: string
}) {
  const errors = problems.filter((p) => p.level === 'error')
  const warnings = problems.filter((p) => p.level !== 'error')

  if (!problems.length) {
    return <p className="small muted">{emptyText}</p>
  }
  return (
    <div className="problems">
      {errors.length ? (
        <p className="small">
          <strong>{errors.length}</strong> {errors.length === 1 ? 'problem' : 'problems'} must
          be fixed before this can be published.
        </p>
      ) : null}
      <ul>
        {[...errors, ...warnings].map((problem, index) => (
          <li key={`${problem.rule}-${index}`} className={problem.level}>
            <span className="mono rule">{problem.rule}</span>
            {problem.where ? <span className="small faint"> · {problem.where}</span> : null}
            <div className="small">{problem.message}</div>
          </li>
        ))}
      </ul>
      {warnings.length ? (
        <p className="small faint">
          {warnings.length} {warnings.length === 1 ? 'warning' : 'warnings'} — reported, and not
          a bar to publishing. These describe the library as the office wrote it.
        </p>
      ) : null}
    </div>
  )
}

/* ------------------------------------------------------- the change note */

export const MIN_CHANGE_NOTE = 20

/**
 * Publishing behind a change note, counted down in front of the officer.
 *
 * Twenty characters is not arbitrary strictness. V1.0 required only a non-empty
 * note and `"."` published cleanly; a year later nobody could say what changed
 * or why forty live cases read differently.
 */
export function PublishBox({
  label,
  busy,
  disabled,
  disabledReason,
  onPublish,
}: {
  label: string
  busy?: boolean
  disabled?: boolean
  disabledReason?: string
  onPublish: (note: string) => void
}) {
  const [note, setNote] = useState('')
  const short = note.trim().length < MIN_CHANGE_NOTE
  const remaining = MIN_CHANGE_NOTE - note.trim().length

  return (
    <div className="publish">
      <label htmlFor="change-note">Change note</label>
      <textarea
        id="change-note"
        rows={2}
        value={note}
        placeholder="What changed, and why."
        onChange={(event) => setNote(event.target.value)}
      />
      <div className="row">
        <button
          type="button"
          disabled={short || busy || disabled}
          onClick={() => onPublish(note)}
        >
          {busy ? 'Publishing…' : label}
        </button>
        <span className="small muted">
          {short
            ? `${remaining} more character${remaining === 1 ? '' : 's'} needed`
            : 'Frozen from the moment this is published.'}
        </span>
      </div>
      {/* A blocked action stays on screen, greyed, with the rule. */}
      {disabled && disabledReason ? (
        <div className="small warn-text">{disabledReason}</div>
      ) : null}
    </div>
  )
}

/* --------------------------------------------------- the condition builder */

type Operand = { path: string } | string | number | boolean | null

interface BuilderState {
  operator: string
  path: string
  value: string
  comparator: string
  n: string
  from: string
  to: string
  documentN: string
}

const EMPTY: BuilderState = {
  operator: 'exists',
  path: '',
  value: '',
  comparator: '>=',
  n: '1',
  from: '',
  to: 'today',
  documentN: '6',
}

/**
 * The declared vocabulary writes a document path as
 * `case.documents.<n>.served_on` — one entry standing for twenty. A rule built
 * from the placeholder reads a path no case supplies, so it is **silently
 * false for ever**, which is the failure this whole product is arranged
 * against. The builder asks which document instead.
 */
const TEMPLATED = '<n>'

function resolvePath(path: string, documentN: string): string {
  return path.includes(TEMPLATED) ? path.replaceAll(TEMPLATED, documentN.trim() || '0') : path
}

/**
 * Build one condition from the twelve operators. **Structured, never free JSON.**
 *
 * A textarea of JSON would be quicker to write and would put the whole catalogue
 * one typo away from a rule that validates and never fires. The operator list
 * and the path list both come off the server's vocabulary endpoint, so this
 * screen cannot offer a thirteenth operator or a path no projection supplies.
 */
export function ConditionBuilder({
  value,
  onChange,
}: {
  value: unknown
  onChange: (condition: unknown) => void
}) {
  const [state, setState] = useState<BuilderState>(EMPTY)
  const vocabulary = useQuery({
    queryKey: ['condition-vocabulary'],
    queryFn: () => api.conditionVocabulary(),
  })

  const condition = buildCondition(state)
  useEffect(() => {
    onChange(condition)
    // The built condition is the whole of what this component produces; the
    // callback identity is the parent's business, not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(condition)])

  const explained = useQuery({
    queryKey: ['explain', JSON.stringify(condition)],
    queryFn: () => api.explainCondition(condition),
    enabled: Boolean(condition),
  })

  const vocab: ConditionVocabulary | undefined = vocabulary.data
  const needsPath = ['exists', 'isTrue', 'equals', 'in', 'count', 'textIncludes'].includes(
    state.operator,
  )
  const needsValue = ['equals', 'in', 'textIncludes'].includes(state.operator)
  const needsComparator = ['count', 'daysBetween'].includes(state.operator)
  const needsDates = ['daysBetween', 'before', 'after'].includes(state.operator)
  // A path standing for twenty documents needs to be told which one.
  const needsDocument = [state.path, state.from, state.to].some((p) => p.includes(TEMPLATED))

  return (
    <div className="builder">
      <div className="row">
        <label>
          Operator
          <select
            value={state.operator}
            aria-label="Operator"
            onChange={(event) => setState({ ...state, operator: event.target.value })}
          >
            {(vocab?.operators ?? []).map((op) => (
              <option key={op} value={op}>
                {op}
              </option>
            ))}
          </select>
        </label>
        {needsPath ? (
          <label>
            Reads
            <select
              value={state.path}
            aria-label="Reads"
              onChange={(event) => setState({ ...state, path: event.target.value })}
            >
              <option value="">choose a fact…</option>
              {(vocab?.paths ?? []).map((entry) => (
                <option key={entry.path} value={entry.path} title={entry.meaning}>
                  {entry.path}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {needsComparator ? (
          <>
            <label>
              Is
              <select
                value={state.comparator}
            aria-label="Comparison"
                onChange={(event) => setState({ ...state, comparator: event.target.value })}
              >
                {(vocab?.comparators ?? []).map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {state.operator === 'daysBetween' ? 'Days' : 'Count'}
              <input
                value={state.n}
            aria-label="Amount"
                inputMode="numeric"
                onChange={(event) => setState({ ...state, n: event.target.value })}
              />
            </label>
          </>
        ) : null}
        {needsValue ? (
          <label>
            Value
            <input
              value={state.value}
            aria-label="Value"
              onChange={(event) => setState({ ...state, value: event.target.value })}
            />
          </label>
        ) : null}
      </div>

      {needsDocument ? (
        <div className="row">
          <label>
            Which document
            <input
              value={state.documentN}
            aria-label="Which document"
              inputMode="numeric"
              onChange={(event) => setState({ ...state, documentN: event.target.value })}
            />
          </label>
          <span className="small faint">
            The vocabulary writes this path as{' '}
            <span className="mono">case.documents.&lt;n&gt;.…</span> — one entry standing for
            twenty. A rule left holding the placeholder would read a path no case supplies and be
            silently false for ever.
          </span>
        </div>
      ) : null}

      {needsDates ? (
        <div className="row">
          <label>
            From
            <select
              value={state.from}
            aria-label="From"
              onChange={(event) => setState({ ...state, from: event.target.value })}
            >
              <option value="">choose a date…</option>
              <option value="today">today</option>
              {(vocab?.paths ?? []).map((entry) => (
                <option key={entry.path} value={entry.path}>
                  {entry.path}
                </option>
              ))}
            </select>
          </label>
          <label>
            To
            <select
              value={state.to}
            aria-label="To"
              onChange={(event) => setState({ ...state, to: event.target.value })}
            >
              <option value="today">today</option>
              {(vocab?.paths ?? []).map((entry) => (
                <option key={entry.path} value={entry.path}>
                  {entry.path}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}

      {/* The English, rendered by the server, live beside the expression —
          so a lawyer can review the catalogue without reading JSON. */}
      <div className="english" data-testid="condition-english">
        <div className="small faint">In English</div>
        <div>{explained.data?.english ?? '—'}</div>
        {explained.data && !explained.data.valid ? (
          <ul className="small warn-text">
            {(explained.data.problems ?? []).map((problem, index) => (
              <li key={index}>{problem}</li>
            ))}
          </ul>
        ) : null}
      </div>
      <details>
        <summary className="small faint">The stored expression</summary>
        <pre className="mono small">{JSON.stringify(condition, null, 2)}</pre>
      </details>
      {value !== undefined && value !== null && !condition ? (
        <p className="small faint">
          This rule already carries a condition; editing replaces it.
        </p>
      ) : null}
    </div>
  )
}

function operand(raw: string, documentN: string): Operand {
  if (!raw) return null
  if (raw === 'today') return 'today'
  const path = resolvePath(raw, documentN)
  return path.startsWith('case.') || path.startsWith('draft.') ? { path } : path
}

function buildCondition(state: BuilderState): unknown {
  const { operator, value, comparator, n, from, to, documentN } = state
  const path = resolvePath(state.path, documentN)
  switch (operator) {
    case 'exists':
      return path ? { exists: path } : null
    case 'isTrue':
      return path ? { isTrue: { path } } : null
    case 'equals':
      return path ? { equals: [{ path }, value] } : null
    case 'in':
      return path ? { in: [{ path }, value.split(',').map((v) => v.trim())] } : null
    case 'textIncludes':
      return path ? { textIncludes: { in: { path }, text: value } } : null
    case 'count':
      return path ? { count: { path, op: comparator, n: Number(n) || 0 } } : null
    case 'daysBetween':
      return from
        ? {
            daysBetween: {
              from: operand(from, documentN),
              to: operand(to, documentN),
              op: comparator,
              days: Number(n) || 0,
            },
          }
        : null
    case 'before':
      return from && to ? { before: [operand(from, documentN), operand(to, documentN)] } : null
    case 'after':
      return from && to ? { after: [operand(from, documentN), operand(to, documentN)] } : null
    case 'not':
      return path ? { not: { exists: path } } : null
    case 'all':
    case 'any':
      return path ? { [operator]: [{ exists: path }] } : null
    default:
      return null
  }
}
